import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { FiLink, FiPlus, FiRefreshCw, FiTrash2, FiX } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { readStoredUser, setActiveBusinessUnitSession } from "../../utils/sessionUser";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import "./PosPurchasingIntegrations.css";

const API = "/api/admin/pos-purchasing-integrations";
const RECEIVER_PATH = "/api/integrations/pos/v1/catalogs/snapshot";
const FIELDS = ["id", "type", "business_unit_id", "source_business_unit_id", "name", "is_active",
  "sync_status", "last_attempt_at", "last_success_at", "item_count", "catalog_count", "sales_start_date", "sales_settings_locked"];
const readConnection = (row) => Object.fromEntries(FIELDS.map((field) => [field, row?.[field]]));
const blankForm = () => ({ type: "pilcore", name: "", business_unit_id: "", source_business_unit_id: "" });
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const salesValues = (row) => ({ date: row.sales_start_date || "" });
const validSalesDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;

export default function PosPurchasingIntegrations() {
  const navigate = useNavigate();
  const { languageId, themeId } = usePmsTheme();
  const t = (es, en) => languageId === "es" ? es : en;
  const [user, setUser] = useState(readStoredUser);
  const authorized = Number(user?.id) === 1;
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [picker, setPicker] = useState(null);
  const [query, setQuery] = useState("");
  const [credential, setCredential] = useState(null);
  const [copied, setCopied] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [catalogAction, setCatalogAction] = useState("keep");
  const [unlinkError, setUnlinkError] = useState(false);
  const [unlinkResult, setUnlinkResult] = useState(null);
  const [salesDrafts, setSalesDrafts] = useState({});
  const [salesFeedback, setSalesFeedback] = useState({});
  const generation = useRef(0);
  const reading = useRef(null);
  const mutationLock = useRef(false);
  const liveUser = useRef(user?.id);
  liveUser.current = user?.id;

  useEffect(() => {
    const update = (event) => {
      if (event.type === "storage" && event.key && !["palantir_user", "palantir_token"].includes(event.key)) return;
      generation.current += 1;
      reading.current = null;
      mutationLock.current = false;
      setUser(readStoredUser());
      setCredential(null);
      setUnlinkTarget(null);
      setUnlinkError(false);
      setUnlinkResult(null);
      setSalesDrafts({});
      setSalesFeedback({});
      setBusy(false);
      setRefreshing(false);
    };
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!authorized || reading.current !== null || mutationLock.current) return;
    const version = generation.current;
    reading.current = version;
    if (!quiet) setRefreshing(true);
    try {
      const [connections, options] = await Promise.all([
        axios.get(API), axios.get(`${API}/options`),
      ]);
      if (version !== generation.current || Number(readStoredUser()?.id) !== 1) return;
      if (!Array.isArray(connections.data?.rows) || !Array.isArray(options.data?.business_units)) throw new Error("Invalid integration response");
      setRows(connections.data.rows.map(readConnection));
      setUnits(options.data.business_units);
      setFailed(false);
    } catch (_error) {
      if (version === generation.current) setFailed(true);
    } finally {
      if (reading.current === version) reading.current = null;
      if (version === generation.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [authorized]);

  useEffect(() => {
    setRows([]);
    setUnits([]);
    setLoading(authorized);
    if (!authorized) return undefined;
    load();
    const timer = window.setInterval(() => load(true), 5000);
    return () => { window.clearInterval(timer); generation.current += 1; };
  }, [authorized, load]);

  const mutate = async (request, { closeEditor = false } = {}) => {
    if (mutationLock.current || Number(readStoredUser()?.id) !== 1) return;
    mutationLock.current = true;
    generation.current += 1;
    const version = generation.current;
    setBusy(true);
    setActionError(false);
    setCredential(null);
    setCopied(false);
    try {
      const response = await request();
      if (version !== generation.current || Number(readStoredUser()?.id) !== 1 || Number(liveUser.current) !== 1) return;
      if (!response.data?.connection?.id) throw new Error("Missing connection");
      const connection = readConnection(response.data.connection);
      setRows((current) => [connection, ...current.filter((row) => row.id !== connection.id)]);
      if (connection.type === "external" && response.data.token) setCredential({ connectionId: connection.id, name: connection.name, token: response.data.token });
      if (closeEditor) { setEditorOpen(false); setForm(blankForm()); }
    } catch (_error) {
      if (version === generation.current) setActionError(true);
    } finally {
      if (version === generation.current) {
        mutationLock.current = false;
        setBusy(false);
        setRefreshing(false);
      }
    }
  };

  const create = (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.business_unit_id || (form.type === "pilcore" && !form.source_business_unit_id)) return;
    const payload = { type: form.type, name: form.name.trim(), business_unit_id: Number(form.business_unit_id) };
    if (form.type === "pilcore") payload.source_business_unit_id = Number(form.source_business_unit_id);
    mutate(() => axios.post(API, payload), { closeEditor: true });
  };
  const changeSales = (row, patch) => {
    setSalesDrafts((current) => ({ ...current, [row.id]: { ...(current[row.id] || salesValues(row)), ...patch } }));
    setSalesFeedback((current) => ({ ...current, [row.id]: null }));
  };
  const saveSales = async (event, row) => {
    event.preventDefault();
    if (mutationLock.current || row.sales_settings_locked || salesFeedback[row.id] === "locked" || Number(readStoredUser()?.id) !== 1) return;
    const draft = salesDrafts[row.id] || salesValues(row);
    if (!validSalesDate(draft.date)) return;
    mutationLock.current = true;
    generation.current += 1;
    reading.current = null;
    const version = generation.current;
    setBusy(true);
    setSalesFeedback((current) => ({ ...current, [row.id]: "saving" }));
    try {
      const response = await axios.patch(`${API}/${row.id}/sales-settings`, { sales_start_date: draft.date });
      if (version !== generation.current || Number(readStoredUser()?.id) !== 1 || Number(liveUser.current) !== 1) return;
      const saved = response.data?.connection;
      if (String(saved?.id) !== String(row.id) || saved.sales_start_date !== draft.date) throw new Error("Invalid sales settings response");
      setRows((current) => current.map((connection) => String(connection.id) === String(row.id) ? readConnection(saved) : connection));
      setSalesDrafts((current) => { const next = { ...current }; delete next[row.id]; return next; });
      setSalesFeedback((current) => ({ ...current, [row.id]: "saved" }));
    } catch (error) {
      if (version === generation.current) setSalesFeedback((current) => ({ ...current, [row.id]: error.response?.status === 409 ? "locked" : "error" }));
    } finally {
      if (version === generation.current) { mutationLock.current = false; setBusy(false); setRefreshing(false); }
    }
  };
  const openUnlink = (row) => {
    if (mutationLock.current || Number(readStoredUser()?.id) !== 1) return;
    setUnlinkTarget(row);
    setCatalogAction("keep");
    setUnlinkError(false);
    setUnlinkResult(null);
  };
  const closeUnlink = () => {
    if (mutationLock.current) return;
    setUnlinkTarget(null);
    setUnlinkError(false);
  };
  const unlink = async () => {
    if (!unlinkTarget || mutationLock.current || Number(readStoredUser()?.id) !== 1) return;
    mutationLock.current = true;
    generation.current += 1;
    reading.current = null;
    const version = generation.current;
    setBusy(true);
    setUnlinkError(false);
    try {
      const response = await axios.delete(`${API}/${unlinkTarget.id}`, { data: { catalog_action: catalogAction } });
      if (version !== generation.current || Number(readStoredUser()?.id) !== 1 || Number(liveUser.current) !== 1) return;
      if (response.data?.deleted !== true || String(response.data.id) !== String(unlinkTarget.id) || response.data.catalog_action !== catalogAction) throw new Error("Invalid unlink response");
      setRows((current) => current.filter((row) => String(row.id) !== String(unlinkTarget.id)));
      setCredential((current) => String(current?.connectionId) === String(unlinkTarget.id) ? null : current);
      setUnlinkResult({ name: unlinkTarget.name, catalogAction });
      setUnlinkTarget(null);
    } catch (_error) {
      if (version === generation.current) setUnlinkError(true);
    } finally {
      if (version === generation.current) {
        mutationLock.current = false;
        setBusy(false);
        setRefreshing(false);
      }
    }
  };
  const openItems = async (row) => {
    const session = readStoredUser();
    if (mutationLock.current || Number(session?.id) !== 1) return;
    const destination = Number(row.business_unit_id);
    mutationLock.current = true;
    setBusy(true);
    setActionError(false);
    try {
      if (Number(session.active_business_unit_id) !== destination) {
        await setActiveBusinessUnitSession(session.id, destination);
      }
      const current = readStoredUser();
      if (String(current?.id) !== String(session.id) || Number(current?.active_business_unit_id) !== destination) throw new Error("Session destination changed");
      navigate("/purchasing/items/pos-catalog");
    } catch (_error) {
      if (Number(readStoredUser()?.id) === 1) setActionError(true);
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  };
  const chooseUnit = (unit) => {
    setForm((current) => ({ ...current, [picker]: String(unit.id),
      ...(picker === "source_business_unit_id" && !current.business_unit_id ? { business_unit_id: String(unit.id) } : {}) }));
    setPicker(null);
    setQuery("");
  };
  const unitName = (id) => units.find((unit) => String(unit.id) === String(id))?.name || `BU ${id}`;
  const formatDate = (value) => {
    const date = value && new Date(value);
    return date && Number.isFinite(date.getTime())
      ? date.toLocaleString(languageId === "es" ? "es-ES" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";
  };
  const statusLabel = (row) => ({
    pending: t("Sincronización pendiente", "Sync pending"),
    waiting: t("Esperando al adaptador", "Waiting for adapter"),
    synced: t("Actualizado", "Up to date"),
    paused: t("En pausa", "Paused"),
    error: t("Error de sincronización", "Sync error"),
  }[row.is_active ? row.sync_status : "paused"] || t("Pendiente", "Pending"));
  const filteredUnits = units.filter((unit) => normalize(`${unit.name} ${unit.id}`).includes(normalize(query.trim())));
  const receiverUrl = new URL(RECEIVER_PATH, new URL(process.env.REACT_APP_API_URL || "/", window.location.origin)).href;

  if (!authorized) return <p className="posIntegration-notice" role="alert">{t("Solo el administrador principal puede configurar las integraciones POS y Compras.", "Only the main administrator can configure POS and Purchasing integrations.")}</p>;

  return (
    <div className="posIntegration" data-theme={themeId}>
      <header className="posIntegration-header">
        <div><h1>{t("POS y Compras", "POS and Purchasing")}</h1>
          <p>{t("Vincula un POS para recibir su catálogo completo y mantenerlo actualizado en Compras.", "Connect a POS to receive its complete catalog and keep it up to date in Purchasing.")}</p></div>
        <div className="posIntegration-actions">
          <button type="button" onClick={() => load()} disabled={loading || refreshing || busy}><FiRefreshCw aria-hidden="true" />{t("Actualizar", "Refresh")}</button>
          <button type="button" className="isPrimary" disabled={loading || busy || !units.length}
            onClick={() => { setForm(blankForm()); setEditorOpen(true); setActionError(false); setCredential(null); }}>
            <FiPlus aria-hidden="true" />{t("Vincular POS", "Connect POS")}
          </button>
        </div>
      </header>
      {failed && <p className="posIntegration-notice isError" role="alert">{t("No se ha podido actualizar la información. Los últimos datos recibidos siguen visibles.", "Could not refresh the information. The last received data remains visible.")}</p>}
      {actionError && <p className="posIntegration-notice isError" role="alert">{t("No se ha podido completar la operación. Revisa la conexión e inténtalo de nuevo.", "Could not complete the operation. Check the connection and try again.")}</p>}
      {unlinkResult && <p className="posIntegration-notice" role="status">{t("Vinculación eliminada", "Connection removed")}: {unlinkResult.name}. {unlinkResult.catalogAction === "keep"
        ? t("Los artículos y catálogos recibidos se conservan en Compras, sin nuevas actualizaciones.", "Received items and catalogs remain in Purchasing without further updates.")
        : t("Se han borrado de Compras los artículos y catálogos recibidos de esta vinculación.", "Items and catalogs received from this connection have been deleted from Purchasing.")}</p>}
      {credential && <section className="posIntegration-secret" aria-label={t("Credencial del adaptador", "Adapter credential")}>
        <div className="posIntegration-cardHeader"><h2>{credential.name}</h2><button type="button" onClick={() => setCredential(null)} aria-label={t("Cerrar credencial", "Close credential")}><FiX aria-hidden="true" /></button></div>
        <p>{t("Copia esta credencial ahora: sólo se muestra al crearla o renovarla. Configúrala en tu adaptador externo.", "Copy this credential now: it is only shown when created or renewed. Configure it in your external adapter.")}</p>
        <label>{t("Credencial", "Credential")}<input readOnly autoComplete="off" spellCheck={false} value={credential.token} /></label>
        <button type="button" onClick={async () => {
          try { await navigator.clipboard.writeText(credential.token); setCopied(true); } catch (_error) { setCopied(false); }
        }}>{copied ? t("Copiada", "Copied") : t("Copiar credencial", "Copy credential")}</button>
      </section>}
      {editorOpen && <form className="posIntegration-editor" onSubmit={create} aria-label={t("Nueva integración POS", "New POS integration")}>
        <div className="posIntegration-cardHeader"><h2>{t("Vincular POS", "Connect POS")}</h2>
          <button type="button" disabled={busy} onClick={() => { setEditorOpen(false); setPicker(null); }} aria-label={t("Cerrar formulario", "Close form")}><FiX aria-hidden="true" /></button></div>
        <fieldset disabled={busy}>
          <legend>{t("Origen del catálogo", "Catalog source")}</legend>
          <div className="posIntegration-types">
            {[["pilcore", t("POS de Pilcore", "Pilcore POS"), t("Importa todos los productos y catálogos de la unidad de origen; mantiene sus cambios automáticamente.", "Imports all products and catalogs from the source unit and automatically keeps their changes in sync.")],
              ["external", t("POS externo", "External POS"), t("Prepara la recepción de catálogos desde un adaptador externo.", "Prepares catalog reception from an external adapter.")]].map(([value, title, description]) => (
              <label key={value} className={form.type === value ? "isSelected" : ""}>
                <input type="radio" name="pos-integration-type" value={value} checked={form.type === value}
                  onChange={() => setForm((current) => ({ ...current, type: value }))} />
                <span><strong>{title}</strong><small>{description}</small></span>
              </label>
            ))}
          </div>
          <div className="posIntegration-formGrid">
            <label>{t("Nombre de la conexión", "Connection name")}<input required maxLength={120} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label>
            {form.type === "pilcore" && <div className="posIntegration-field"><span>{t("Unidad de origen · POS", "Source unit · POS")}</span>
              <button type="button" aria-haspopup="dialog" onClick={() => { setPicker("source_business_unit_id"); setQuery(""); }}>
                {form.source_business_unit_id ? unitName(form.source_business_unit_id) : t("Elegir unidad de origen", "Choose source unit")}
              </button></div>}
            <div className="posIntegration-field"><span>{t("Unidad de destino · Compras", "Destination unit · Purchasing")}</span>
              <button type="button" aria-haspopup="dialog" onClick={() => { setPicker("business_unit_id"); setQuery(""); }}>
                {form.business_unit_id ? unitName(form.business_unit_id) : t("Elegir unidad de destino", "Choose destination unit")}
              </button></div>
          </div>
          <p className="posIntegration-hint">{form.type === "pilcore"
            ? t("Al activar se importa el catálogo completo. Las siguientes altas, cambios y bajas se sincronizan automáticamente, sin mantener esta pantalla abierta.", "Activation imports the complete catalog. Subsequent additions, changes and removals sync automatically, without keeping this page open.")
            : t("Elegir POS externo no conecta un proveedor por sí solo. Necesitas un adaptador que envíe el catálogo completo al endpoint con la credencial generada.", "Choosing an external POS does not connect a provider on its own. You need an adapter that sends the complete catalog to the endpoint using the generated credential.")}</p>
          <button className="isPrimary" type="submit" disabled={!form.name.trim() || !form.business_unit_id || (form.type === "pilcore" && !form.source_business_unit_id)}>
            {busy ? t("Guardando…", "Saving…") : form.type === "pilcore" ? t("Activar sincronización", "Activate sync") : t("Crear conexión externa", "Create external connection")}
          </button>
        </fieldset>
      </form>}
      {loading ? <p className="posIntegration-notice" role="status">{t("Cargando conexiones…", "Loading connections…")}</p>
        : !rows.length && !failed ? <p className="posIntegration-notice" role="status">{t("Todavía no hay conexiones POS configuradas.", "No POS connections have been configured yet.")}</p>
          : <div className="posIntegration-connections">{rows.map((row) => {
            const salesLocked = Boolean(row.sales_settings_locked) || salesFeedback[row.id] === "locked";
            const sales = salesLocked ? salesValues(row) : salesDrafts[row.id] || salesValues(row);
            const salesChanged = sales.date !== (row.sales_start_date || "");
            return <article key={row.id} className="posIntegration-card" aria-label={row.name}>
            <div className="posIntegration-cardHeader"><div><h2>{row.name}</h2><p>{row.type === "pilcore" ? t("POS de Pilcore", "Pilcore POS") : t("POS externo", "External POS")}</p></div>
              <span className={`posIntegration-state is-${row.is_active ? row.sync_status : "paused"}`}>{statusLabel(row)}</span></div>
            <dl>
              {row.type === "pilcore" && <div><dt>{t("Origen", "Source")}</dt><dd>{unitName(row.source_business_unit_id)}</dd></div>}
              <div><dt>{t("Compras de destino", "Purchasing destination")}</dt><dd>{unitName(row.business_unit_id)}</dd></div>
              <div><dt>{t("Artículos / catálogos", "Items / catalogs")}</dt><dd>{Number(row.item_count || 0)} / {Number(row.catalog_count || 0)}</dd></div>
              <div><dt>{t("Última actualización correcta", "Last successful update")}</dt><dd>{formatDate(row.last_success_at)}</dd></div>
              {row.sync_status === "error" && <div><dt>{t("Último intento", "Last attempt")}</dt><dd>{formatDate(row.last_attempt_at)}</dd></div>}
            </dl>
            <form className="posIntegration-sales" onSubmit={(event) => saveSales(event, row)} aria-label={`${t("Recepción de ventas", "Sales reception")}: ${row.name}`}>
              <fieldset disabled={busy || salesLocked}>
                <legend>{t("Recepción de ventas", "Sales reception")}</legend>
                <p>{row.sales_start_date
                  ? t(`Se recibirán ventas desde ${row.sales_start_date}, incluida esa fecha.`, `Sales will be received from ${row.sales_start_date}, including that date.`)
                  : t("No se recibirán ventas hasta configurar la fecha.", "No sales will be received until a start date is configured.")}</p>
                <div className="posIntegration-salesFields">
                  <label>{t("Desde esta fecha (incluida)", "From this date (inclusive)")}<input type="date" required value={sales.date} onChange={(event) => changeSales(row, { date: event.target.value })} /></label>
                  <button type="submit" className="isPrimary" disabled={!validSalesDate(sales.date) || !salesChanged}>
                    {salesFeedback[row.id] === "saving" ? t("Guardando…", "Saving…") : t("Guardar configuración de ventas", "Save sales settings")}
                  </button>
                </div>
              </fieldset>
              <p>{t("El almacén del que descontar se elige en cada vínculo de Artículos del POS.", "Choose the inventory location to deduct from in each POS item link.")}</p>
              {salesLocked && <p className="posIntegration-salesLocked" role={salesFeedback[row.id] === "locked" ? "alert" : undefined}>{t("La fecha no se puede cambiar porque ya existen movimientos de inventario de estas ventas.", "The date cannot be changed because inventory movements already exist for these sales.")}</p>}
              {salesFeedback[row.id] === "saved" && <p role="status">{t("Configuración de ventas guardada.", "Sales settings saved.")}</p>}
              {salesFeedback[row.id] === "error" && <p className="posIntegration-notice isError" role="alert">{t("No se ha podido guardar la configuración de ventas. Revisa la conexión e inténtalo de nuevo.", "Could not save sales settings. Check the connection and try again.")}</p>}
            </form>
            {row.type === "external" && <details className="posIntegration-adapter"><summary>{t("Configurar adaptador externo", "Configure external adapter")}</summary>
              <p>{t("El adaptador debe enviar el catálogo completo con la credencial de esta conexión.", "The adapter must send the complete catalog using this connection’s credential.")}</p>
              <code>POST {receiverUrl}</code>
              <code>Authorization: Bearer &lt;{t("credencial", "credential")}&gt;</code>
              <p>{t("Envía instantáneas completas con una revisión creciente. Puedes reintentar la misma revisión con el mismo contenido.", "Send complete snapshots with an increasing revision. You can retry the same revision with identical content.")}</p>
              <p>{t("Renovar la credencial invalida la anterior y requiere actualizar el adaptador.", "Renewing the credential invalidates the previous one and requires updating the adapter.")}</p>
            </details>}
            <div className="posIntegration-actions">
              <button type="button" disabled={busy} onClick={() => openItems(row)}
                aria-label={`${t("Ver artículos de", "View items for")} ${unitName(row.business_unit_id)}`}>
                <FiLink aria-hidden="true" />{t("Ver artículos en Compras", "View items in Purchasing")}
              </button>
              <button type="button" disabled={busy} onClick={() => mutate(() => axios.patch(`${API}/${row.id}`, { is_active: !row.is_active }))}>{row.is_active ? t("Pausar", "Pause") : t("Reanudar", "Resume")}</button>
              {row.type === "pilcore" ? <button type="button" disabled={busy || !row.is_active} onClick={() => mutate(() => axios.post(`${API}/${row.id}/sync`, {}))}>{t("Sincronizar ahora", "Sync now")}</button>
                : <button type="button" disabled={busy} onClick={() => {
                  if (window.confirm(t("La credencial anterior dejará de funcionar. ¿Quieres generar una nueva para actualizar el adaptador?", "The previous credential will stop working. Generate a new one to update the adapter?"))) mutate(() => axios.post(`${API}/${row.id}/rotate-token`, {}));
                }}>{t("Renovar credencial", "Renew credential")}</button>}
              <button type="button" className="isDanger" disabled={busy} onClick={() => openUnlink(row)}>
                <FiTrash2 aria-hidden="true" />{t("Eliminar vinculación", "Remove connection")}
              </button>
            </div>
          </article>; })}</div>}
      <Modal show={Boolean(unlinkTarget)} onHide={closeUnlink} centered animation={false} backdrop={busy ? "static" : true} keyboard={!busy}
        dialogClassName="posIntegration-unlinkDialog" contentClassName="posIntegration-unlinkContent" aria-labelledby="pos-integration-unlink-title" aria-describedby="pos-integration-unlink-description">
        <div className="posIntegration posIntegration-unlink" data-theme={themeId}>
          <Modal.Header><Modal.Title id="pos-integration-unlink-title">{t("Eliminar vinculación", "Remove connection")}</Modal.Title>
            <button type="button" disabled={busy} onClick={closeUnlink} aria-label={t("Cerrar confirmación", "Close confirmation")}><FiX aria-hidden="true" /></button>
          </Modal.Header>
          <Modal.Body>
            <p id="pos-integration-unlink-description"><strong>{unlinkTarget?.name}</strong> · {t("Compras de destino", "Purchasing destination")}: {unlinkTarget && unitName(unlinkTarget.business_unit_id)}.</p>
            <p>{t("Se detendrá la sincronización y se revocarán las credenciales. Esta vinculación no podrá recuperarse.", "Synchronization will stop and credentials will be revoked. This connection cannot be restored.")}</p>
            <p>{t("Los productos y catálogos del POS de origen no se modificarán.", "Products and catalogs in the source POS will remain unchanged.")}</p>
            <fieldset disabled={busy}>
              <legend>{t("Datos recibidos en Compras", "Data received in Purchasing")}</legend>
              <div className="posIntegration-types">
                {[["keep", t("Conservar artículos y catálogos", "Keep items and catalogs"), t("Seguirán visibles en Compras con sus configuraciones y vínculos con recetas, sin nuevas actualizaciones de este POS.", "They will remain visible in Purchasing with their settings and recipe links, without further updates from this POS.")],
                  ["delete", t("Borrar artículos y catálogos", "Delete items and catalogs"), t("Borra de Compras los datos recibidos de esta vinculación, incluidas sus configuraciones y vínculos con recetas. Las recetas originales se conservan. Esta acción no se puede deshacer.", "Deletes data received from this connection from Purchasing, including its settings and recipe links. Original recipes are preserved. This action cannot be undone.")]].map(([value, title, description]) => (
                  <label key={value} className={catalogAction === value ? "isSelected" : ""}>
                    <input type="radio" name="pos-integration-catalog-action" value={value} checked={catalogAction === value} onChange={() => setCatalogAction(value)} />
                    <span><strong>{title}</strong><small>{description}</small></span>
                  </label>
                ))}
              </div>
            </fieldset>
            {unlinkError && <p className="posIntegration-notice isError" role="alert">{t("No se ha podido confirmar la eliminación. Revisa la conexión e inténtalo de nuevo.", "Could not confirm removal. Check the connection and try again.")}</p>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" disabled={busy} onClick={closeUnlink}>{t("Cancelar", "Cancel")}</button>
            <button type="button" className="isDanger" disabled={busy} onClick={unlink}>{busy ? t("Eliminando…", "Removing…") : t("Confirmar eliminación", "Confirm removal")}</button>
          </Modal.Footer>
        </div>
      </Modal>
      <Modal show={Boolean(picker)} onHide={() => setPicker(null)} centered animation={false} dialogClassName="posIntegration-picker" contentClassName="posIntegration-pickerContent" aria-labelledby="pos-integration-picker-title">
        <Modal.Header closeButton><Modal.Title id="pos-integration-picker-title">{picker === "source_business_unit_id" ? t("Elegir unidad de origen", "Choose source unit") : t("Elegir unidad de destino", "Choose destination unit")}</Modal.Title></Modal.Header>
        <Modal.Body><label>{t("Buscar unidad por nombre o ID", "Search units by name or ID")}<input autoFocus type="search" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
          <div className="posIntegration-options">{filteredUnits.slice(0, 60).map((unit) => <button type="button" key={unit.id} onClick={() => chooseUnit(unit)}><strong>{unit.name}</strong><small>BU {unit.id}</small></button>)}</div>
          {!filteredUnits.length && <p role="status">{t("No hay unidades que coincidan.", "No matching units.")}</p>}
          {filteredUnits.length > 60 && <p>{t("Afina la búsqueda para ver más unidades.", "Refine your search to find more units.")}</p>}
        </Modal.Body>
      </Modal>
    </div>
  );
}
